import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { formatRupiah, hasBillingRole, canBillingManage, periodeLabel, receivedLabel } from "@/lib/billing";
import { Download, FileSpreadsheet, RefreshCw, Wallet } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * BillingRekap — halaman rekap billing untuk admin/billing/kolektor
 * (kolektor otomatis scoped). Include download Excel.
 */
export default function BillingRekap() {
  const { user } = useAuth();
  const now = new Date();
  const [periode, setPeriode] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`);
  const [wilayahList, setWilayahList] = useState([]);
  const [kolektorList, setKolektorList] = useState([]);
  const [wilayahId, setWilayahId] = useState("");
  const [kolektorUserId, setKolektorUserId] = useState("");
  const [statusF, setStatusF] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const canManage = canBillingManage(user);
  const isKolektorRole = user?.role === "kolektor";


  const load = async () => {
    if (!hasBillingRole(user)) return;
    setLoading(true);
    try {
      const params = {};
      if (periode) params.periode_tagihan = periode;
      if (wilayahId) params.wilayah_id = wilayahId;
      if (kolektorUserId) params.kolektor_user_id = kolektorUserId;
      if (statusF) params.status = statusF;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const { data: r } = await api.get("/billing/rekap", { params });
      setData(r);
      // Section "Uang yang Saya Terima" dihapus Feb 2026 — fetch TUNAI kolektor
      // ikut dibersihkan (tidak dipakai lagi).
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Gagal memuat rekap");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (canManage) {
      api.get("/billing/wilayah").then(({ data }) => setWilayahList(data.items || [])).catch(() => {});
      api.get("/users").then(({ data }) => {
        // Feb 2026 — audit filter Kolektor:
        //   Dropdown dulu hanya memfilter `extra_roles.includes("kolektor")`,
        //   sehingga user yang `role` primary-nya `kolektor` TIDAK muncul
        //   (contoh Production: "Yanti @yanti" yang memiliki 156 invoice tidak
        //   muncul). Fix: terima user yang `role === "kolektor"` ATAU
        //   `extra_roles.includes("kolektor")`.
        const items = Array.isArray(data) ? data : (data?.items || []);
        setKolektorList(items.filter((u) => u.role === "kolektor" || (u.extra_roles || []).includes("kolektor")));
      }).catch(() => setKolektorList([]));
    }
    load();
    // eslint-disable-next-line
  }, []);

  const downloadExcel = async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      const params = new URLSearchParams();
      if (periode) params.append("periode_tagihan", periode);
      if (wilayahId) params.append("wilayah_id", wilayahId);
      if (kolektorUserId) params.append("kolektor_user_id", kolektorUserId);
      if (statusF) params.append("status", statusF);
      if (dateFrom) params.append("date_from", dateFrom);
      if (dateTo) params.append("date_to", dateTo);
      const url = `${BACKEND_URL}/api/billing/rekap/export.xlsx?${params.toString()}`;
      // Menggunakan fetch supaya bisa attach Authorization header
      const token = localStorage.getItem("token");
      const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      const filename = (resp.headers.get("content-disposition") || "").match(/filename="([^"]+)"/)?.[1] || `billing_rekap_${periode || "all"}.xlsx`;
      const a = document.createElement("a");
      const objUrl = URL.createObjectURL(blob);
      a.href = objUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objUrl);
      toast.success("Excel berhasil diunduh");
    } catch (err) {
      toast.error("Gagal mengunduh Excel: " + err.message);
    } finally { setDownloading(false); }
  };

  if (!hasBillingRole(user)) {
    return <div className="p-6" data-testid="rekap-denied">Anda tidak memiliki akses ke Billing.</div>;
  }

  const s = data?.summary || {};
  // Feb 2026 — Rentang mode = periode kosong & salah satu date_from/date_to
  // terisi. UI mode-aware (label Total Dibayar vs Total Diterima + 2 card
  // tambahan). Nilai yang digunakan di card tetap datang dari API.
  const rentangMode = (!periode) && (!!dateFrom || !!dateTo);

  return (
    <div className="p-4 sm:p-6 space-y-4" data-testid="billing-rekap-page">
      <header className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div>
          <h1 className="text-2xl sm:text-3xl font-black tracking-tight">Rekap Billing</h1>
          <p className="text-sm text-[#6B7280]">Ringkasan tagihan & pembayaran{periode ? ` — ${periodeLabel(periode)}` : ""}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="rekap-refresh">
            <RefreshCw className="w-3 h-3 mr-1" /> Refresh
          </Button>
          <Button size="sm" onClick={downloadExcel} disabled={downloading} data-testid="rekap-download-excel">
            <FileSpreadsheet className="w-3 h-3 mr-1" /> {downloading ? "Mengunduh…" : "Export Excel"}
          </Button>
        </div>
      </header>

      {/* Filter panel */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 border rounded-md p-3 bg-[#F8F9FA]"
           data-testid="rekap-filters">
        <div><Label>Periode</Label>
          <Input type="month" value={periode}
                 onChange={(e) => {
                   // Feb 2026 — mutual exclusive dgn Rentang Pembayaran.
                   // Mengganti Periode akan mengosongkan Dari/S/d otomatis.
                   setPeriode(e.target.value);
                   setDateFrom("");
                   setDateTo("");
                 }}
                 data-testid="rekap-periode" /></div>
        {canManage && (
          <div><Label>Wilayah</Label>
            <select value={wilayahId} onChange={(e) => setWilayahId(e.target.value)}
                    className="border rounded-md px-3 py-2 w-full text-sm bg-white"
                    data-testid="rekap-wilayah">
              <option value="">Semua</option>
              {wilayahList.map((w) => <option key={w.wilayah_id} value={w.wilayah_id}>{w.nama_wilayah}</option>)}
            </select></div>
        )}
        {canManage && (
          <div><Label>Kolektor</Label>
            <select value={kolektorUserId} onChange={(e) => setKolektorUserId(e.target.value)}
                    className="border rounded-md px-3 py-2 w-full text-sm bg-white"
                    data-testid="rekap-kolektor">
              <option value="">Semua</option>
              <option value="KOLEKTOR-SYSTEM">Kolektor Sistem</option>
              {kolektorList.map((u) => <option key={u.id} value={u.id}>{u.name} (@{u.username})</option>)}
            </select></div>
        )}
        <div><Label>Status</Label>
          <select value={statusF} onChange={(e) => setStatusF(e.target.value)}
                  className="border rounded-md px-3 py-2 w-full text-sm bg-white"
                  data-testid="rekap-status">
            <option value="">Semua</option>
            <option value="BELUM_LUNAS">Belum Lunas</option>
            <option value="SEBAGIAN">Sebagian</option>
            <option value="LUNAS">Lunas</option>
          </select></div>
        <div><Label>Pembayaran Dari</Label>
          <Input type="date" value={dateFrom}
                 onChange={(e) => {
                   // Feb 2026 — mutual exclusive dgn Periode. Mengubah
                   // Dari/S/d otomatis mengosongkan Periode.
                   setDateFrom(e.target.value);
                   if (e.target.value) setPeriode("");
                 }}
                 data-testid="rekap-date-from" /></div>
        <div><Label>Pembayaran s/d</Label>
          <Input type="date" value={dateTo}
                 onChange={(e) => {
                   setDateTo(e.target.value);
                   if (e.target.value) setPeriode("");
                 }}
                 data-testid="rekap-date-to" /></div>
        <div className="col-span-2 sm:col-span-3 lg:col-span-6 pt-1 flex items-center gap-3">
          <Button size="sm" onClick={load} data-testid="rekap-apply">Terapkan Filter</Button>
          <div className="text-[11px] text-[#6B7280]" data-testid="rekap-filter-hint">
            Gunakan Periode Tagihan atau Rentang Pembayaran, bukan keduanya.
          </div>
        </div>
      </div>

      {loading && <div className="text-sm text-[#6B7280]">Memuat rekap…</div>}
      {data && (
        <>
          {/* Feb 2026 Phase 13/B3 — Kolektor mobile-first summary. */}
          {isKolektorRole && (
            <div className="space-y-3" data-testid="rekap-kolektor-panel">
              <div className="rounded-xl border-2 border-[#0055FF] bg-gradient-to-br from-[#0055FF] to-[#003DB2] text-white p-5 shadow-lg"
                   data-testid="kolektor-diterima-saya-card">
                <div className="flex items-center gap-2 text-sm opacity-90">
                  <Wallet className="w-4 h-4" /> Diterima Saya (Tunai)
                </div>
                <div className="text-3xl sm:text-4xl font-black mt-2 font-mono"
                     data-testid="kolektor-diterima-saya-value">
                  {formatRupiah((data.per_kolektor?.find(k => k.collector_user_id === user.id)?.total_diterima_saya) ?? 0)}
                </div>
                <div className="text-[11px] opacity-80 mt-1">
                  Uang tunai yang tercatat diterima oleh Anda. Cocokkan dengan uang fisik di tangan.
                </div>
              </div>
              {/* Feb 2026 — Dua card ("Dibayar ke Admin" & "Total Pembayaran")
                  dihapus sesuai permintaan user. Data agregat tetap tersedia
                  via `data.per_kolektor` + `s.total_dibayar` untuk konsumen
                  lain / export. Hero card "Diterima Saya (Tunai)" tetap. */}

              {/* Section "Uang yang Saya Terima" dihapus Feb 2026 sesuai permintaan user.
                  Ringkasan Rekap, filter periode, cards, rincian kanal, konsolidasi,
                  dan export Excel tetap utuh. Backend endpoint tidak diubah. */}
            </div>
          )}

          {/* Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="rekap-summary-cards">
            <SummaryCard label="Total Invoice" value={s.total_invoice ?? 0} />
            <SummaryCard label="Total Tagihan" value={formatRupiah(s.total_tagihan)} mono testId="rekap-sum-tagihan" />
            <SummaryCard label="Total Diskon" value={formatRupiah(s.total_diskon)} mono />
            <SummaryCard label="Dibayar atas Tagihan" value={formatRupiah(s.total_dibayar)} mono ok testId="rekap-sum-dibayar"
                         hint="Pembayaran dialokasikan ke invoice dalam cakupan laporan." />
            <SummaryCard label="Sisa Tagihan" value={formatRupiah(s.total_sisa)} mono danger testId="rekap-sum-sisa-tagihan"
                         hint="Tagihan − Dibayar atas Tagihan." />
            <SummaryCard label="Bon Ter-carry" value={formatRupiah(s.total_bon_carried)} mono />
            <SummaryCard label="Penerimaan Kas" value={formatRupiah(s.total_penerimaan_kas ?? s.total_payment_period ?? 0)} mono testId="rekap-sum-penerimaan-kas"
                         hint="Σ pembayaran CONFIRMED berdasarkan tanggal bayar. Termasuk pelunasan invoice periode lain." />
            <SummaryCard label="Payment Kolektor" value={formatRupiah(s.total_channel_kolektor ?? 0)} mono testId="rekap-sum-kolektor" />
            <SummaryCard label="Payment Transfer" value={formatRupiah(s.total_channel_transfer ?? 0)} mono testId="rekap-sum-transfer" />
            <SummaryCard label="Payment Loket" value={formatRupiah(s.total_channel_loket ?? 0)} mono testId="rekap-sum-loket" />
            {rentangMode && (
              <>
                <SummaryCard label="Pembayaran Invoice Periode" value={formatRupiah(s.total_payment_invoice_periode ?? 0)} mono testId="rekap-sum-pay-periode" />
                <SummaryCard label="Pembayaran Carry-forward" value={formatRupiah(s.total_payment_carry_forward ?? 0)} mono testId="rekap-sum-pay-carry" />
              </>
            )}
            <KonsolidasiCard
              count={s.total_konsolidasi_count ?? 0}
              amount={s.total_konsolidasi_amount ?? 0}
              kolektor={s.total_konsolidasi_channel_kolektor ?? 0}
              transfer={s.total_konsolidasi_channel_transfer ?? 0}
              loket={s.total_konsolidasi_channel_loket ?? 0}
            />
          </div>
          {/* Feb 2026 — Rekonsiliasi guard. reconcile_diff != 0 menandakan
              data drift (manual edit, legacy payment). Tampilkan banner
              informatif supaya auditor sadar. */}
          {s.reconcile_diff !== undefined && s.reconcile_diff !== 0 && (
            <div className="rounded-md border border-[#F59E0B] bg-[#FFFBEB] p-3 text-xs text-[#92400E]"
                 data-testid="rekap-reconcile-warning">
              <b>Perhatian:</b> Rekonsiliasi tidak eksak — Tagihan − Dibayar − Sisa = {formatRupiah(s.reconcile_diff)}.
              Kemungkinan ada invoice dengan data drift (edit manual, payment pre-engine).
              Dapat diaudit via Export Excel sheet "Invoice".
            </div>
          )}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatusBox label="Lunas" value={s.count_lunas} color="bg-[#D1FAE5] text-[#065F46]" />
            <StatusBox label="Sebagian" value={s.count_sebagian} color="bg-[#FEF3C7] text-[#92400E]" />
            <StatusBox label="Belum Lunas" value={s.count_belum_lunas} color="bg-[#FEE2E2] text-[#991B1B]" />
            <StatusBox label="Dibatalkan" value={s.count_dibatalkan} color="bg-[#E5E7EB] text-[#374151]" />
          </div>

          {/* Rekap per-kolektor (admin/billing) */}
          {canManage && (data.per_kolektor?.length ?? 0) > 0 && (
            <div className="border rounded-md p-3" data-testid="rekap-per-kolektor">
              <h2 className="font-black text-lg mb-2">Rekap per Kolektor</h2>
              {/* Desktop table (md+) */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="border-b bg-[#F8F9FA]">
                      <th className="px-3 py-2 text-left">Kolektor</th>
                      <th className="px-3 py-2 text-right">Customer</th>
                      <th className="px-3 py-2 text-right">Invoice</th>
                      <th className="px-3 py-2 text-right">Total Tagihan</th>
                      <th className="px-3 py-2 text-right" title="Pembayaran dialokasikan ke invoice dalam cakupan">Dibayar</th>
                      <th className="px-3 py-2 text-right">Sisa</th>
                      <th className="px-3 py-2 text-right" title="Σ pembayaran berdasarkan payment_date (arus kas)">Pen. Kas</th>
                      <th className="px-3 py-2 text-right">Kolektor</th>
                      <th className="px-3 py-2 text-right">Transfer</th>
                      <th className="px-3 py-2 text-right">Loket</th>
                      <th className="px-3 py-2 text-right">Lunas</th>
                      <th className="px-3 py-2 text-right">Sebagian</th>
                      <th className="px-3 py-2 text-right">Belum</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.per_kolektor.map((k) => (
                      <tr key={k.collector_user_id || "_x"} className="border-b" data-testid={`rekap-kol-${k.collector_user_id || 'unassigned'}`}>
                        <td className="px-3 py-2">{k.kolektor_name} {k.kolektor_username ? `(@${k.kolektor_username})` : ""}</td>
                        <td className="px-3 py-2 text-right">{k.count_customer}</td>
                        <td className="px-3 py-2 text-right">{k.count_invoice}</td>
                        <td className="px-3 py-2 text-right font-mono text-xs">{formatRupiah(k.total_tagihan)}</td>
                        <td className="px-3 py-2 text-right font-mono text-xs">{formatRupiah(k.total_dibayar)}</td>
                        <td className="px-3 py-2 text-right font-mono text-xs">{formatRupiah(k.total_sisa)}</td>
                        <td className="px-3 py-2 text-right font-mono text-xs">{formatRupiah(k.total_penerimaan_kas ?? 0)}</td>
                        <td className="px-3 py-2 text-right font-mono text-xs" data-testid={`rekap-ch-kolektor-${k.collector_user_id || 'unassigned'}`}>{formatRupiah(k.channel_kolektor ?? 0)}</td>
                        <td className="px-3 py-2 text-right font-mono text-xs" data-testid={`rekap-ch-transfer-${k.collector_user_id || 'unassigned'}`}>{formatRupiah(k.channel_transfer ?? 0)}</td>
                        <td className="px-3 py-2 text-right font-mono text-xs" data-testid={`rekap-ch-loket-${k.collector_user_id || 'unassigned'}`}>{formatRupiah(k.channel_loket ?? 0)}</td>
                        <td className="px-3 py-2 text-right">{k.count_lunas}</td>
                        <td className="px-3 py-2 text-right">{k.count_sebagian}</td>
                        <td className="px-3 py-2 text-right">{k.count_belum_lunas}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {/* Mobile card list (<md) — Aug 2026 responsive rework */}
              <div className="md:hidden space-y-2" data-testid="rekap-per-kolektor-mobile">
                {data.per_kolektor.map((k) => (
                  <div key={k.collector_user_id || "_x"}
                        className="border rounded-md bg-white p-3 space-y-1"
                        data-testid={`rekap-kol-mobile-${k.collector_user_id || 'unassigned'}`}>
                    <div className="pb-1 border-b">
                      <div className="font-semibold text-sm">{k.kolektor_name}</div>
                      {k.kolektor_username && (
                        <div className="text-[11px] text-[#6B7280]">@{k.kolektor_username}</div>
                      )}
                    </div>
                    <div className="text-xs space-y-1 pt-1">
                      <div className="flex justify-between"><span className="text-[#6B7280]">Customer</span><span className="font-semibold">{k.count_customer}</span></div>
                      <div className="flex justify-between"><span className="text-[#6B7280]">Invoice</span><span className="font-semibold">{k.count_invoice}</span></div>
                      <div className="flex justify-between"><span className="text-[#6B7280]">Total Tagihan</span><span className="font-mono">{formatRupiah(k.total_tagihan)}</span></div>
                      <div className="flex justify-between"><span className="text-[#6B7280]">Dibayar</span><span className="font-mono">{formatRupiah(k.total_dibayar)}</span></div>
                      <div className="flex justify-between"><span className="text-[#6B7280]">Sisa</span><span className={`font-mono ${k.total_sisa > 0 ? "text-[#991B1B]" : ""}`}>{formatRupiah(k.total_sisa)}</span></div>
                      <div className="flex justify-between"><span className="text-[#6B7280]">Pen. Kas</span><span className="font-mono">{formatRupiah(k.total_penerimaan_kas ?? 0)}</span></div>
                      <div className="flex justify-between"><span className="text-[#6B7280]">Kolektor</span><span className="font-mono">{formatRupiah(k.channel_kolektor ?? 0)}</span></div>
                      <div className="flex justify-between"><span className="text-[#6B7280]">Transfer</span><span className="font-mono">{formatRupiah(k.channel_transfer ?? 0)}</span></div>
                      <div className="flex justify-between"><span className="text-[#6B7280]">Loket</span><span className="font-mono">{formatRupiah(k.channel_loket ?? 0)}</span></div>
                      <div className="flex gap-2 pt-1">
                        <span className="flex-1 rounded px-2 py-1 text-[10px] text-center bg-[#D1FAE5] text-[#065F46]">Lunas: <b>{k.count_lunas}</b></span>
                        <span className="flex-1 rounded px-2 py-1 text-[10px] text-center bg-[#FEF3C7] text-[#92400E]">Sebagian: <b>{k.count_sebagian}</b></span>
                        <span className="flex-1 rounded px-2 py-1 text-[10px] text-center bg-[#FEE2E2] text-[#991B1B]">Belum: <b>{k.count_belum_lunas}</b></span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function SummaryCard({ label, value, mono, ok, danger, testId, hint }) {
  return (
    <div className="border rounded-lg p-4 bg-white" data-testid={testId} title={hint || undefined}>
      <div className="text-xs text-[#6B7280]">{label}</div>
      <div className={`text-lg sm:text-xl font-black mt-1 ${mono ? "font-mono" : ""} ${danger ? "text-[#991B1B]" : ok ? "text-[#065F46]" : ""}`}>{value}</div>
      {hint && <div className="text-[10px] text-[#6B7280] mt-1 leading-snug">{hint}</div>}
    </div>
  );
}

/**
 * KonsolidasiCard — card audit trail "Transaksi Konsolidasi".
 * Spans 2 columns pada grid 4-col untuk muat breakdown channel.
 * Angka di card ini bersifat INFORMATIONAL — subset payment yang sudah
 * tercakup di Total Dibayar, jadi JANGAN dijumlahkan ulang.
 */
function KonsolidasiCard({ count, amount, kolektor, transfer, loket }) {
  return (
    <div className="col-span-2 border rounded-lg p-4 bg-white"
         data-testid="rekap-sum-konsolidasi">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-xs text-[#6B7280]">Transaksi Konsolidasi</div>
          <div className="text-lg sm:text-xl font-black mt-1 font-mono"
               data-testid="rekap-sum-konsolidasi-amount">
            {formatRupiah(amount)}
          </div>
          <div className="text-[10px] text-[#6B7280]">
            Subset Penerimaan Kas pada invoice bon/carry-forward. Tidak selalu masuk Dibayar atas Tagihan; pembayaran pada invoice sumber hanya tercatat di Penerimaan Kas.
          </div>
        </div>
        <div className="shrink-0 text-right">
          <div className="text-[10px] text-[#6B7280]">Transaksi</div>
          <div className="text-base font-black" data-testid="rekap-sum-konsolidasi-count">
            {count}
          </div>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2 mt-3 pt-3 border-t text-xs">
        <div data-testid="rekap-sum-konsolidasi-kolektor">
          <div className="text-[#6B7280]">Kolektor</div>
          <div className="font-mono font-semibold">{formatRupiah(kolektor)}</div>
        </div>
        <div data-testid="rekap-sum-konsolidasi-transfer">
          <div className="text-[#6B7280]">Transfer</div>
          <div className="font-mono font-semibold">{formatRupiah(transfer)}</div>
        </div>
        <div data-testid="rekap-sum-konsolidasi-loket">
          <div className="text-[#6B7280]">Loket</div>
          <div className="font-mono font-semibold">{formatRupiah(loket)}</div>
        </div>
      </div>
    </div>
  );
}

function StatusBox({ label, value, color }) {
  return (
    <div className={`rounded-lg p-4 ${color}`}>
      <div className="text-xs">{label}</div>
      <div className="text-2xl font-black">{value ?? 0}</div>
    </div>
  );
}
